"use client";

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import {
	getPeptideName,
	getPeptideCategory,
	getPeptideHalfLifeSource,
	getPeptideHalfLifeSourceLanguage,
} from "@/lib/calculator/peptide-data";
import type { PeptideData } from "@/lib/calculator/types";
import type { Locale } from "@/config/locale";
import { translateTimeUnits } from "@/lib/peptide-translations";

interface HalfLifePeptideSelectorProps {
	peptides: PeptideData[];
	value: string;
	onChange: (slug: string) => void;
	locale: Locale;
	placeholder: string;
	searchPlaceholder: string;
	emptyLabel: string;
}

export function HalfLifePeptideSelector({
	peptides,
	value,
	onChange,
	locale,
	placeholder,
	searchPlaceholder,
	emptyLabel,
}: HalfLifePeptideSelectorProps) {
	const [open, setOpen] = useState(false);
	const [present, setPresent] = useState(false);
	const [search, setSearch] = useState("");
	const containerRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const searchRef = useRef<HTMLInputElement>(null);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const selected = peptides.find((p) => p.slug === value);
	useLayoutEffect(() => {
		if (open) searchRef.current?.focus();
	}, [open]);
	const show = () => {
		if (closeTimer.current) clearTimeout(closeTimer.current);
		setSearch("");
		setPresent(true);
		setOpen(true);
	};
	const hide = useCallback((returnFocus = false) => {
		setOpen(false);
		if (returnFocus) triggerRef.current?.focus();
		if (closeTimer.current) clearTimeout(closeTimer.current);
		const cssDuration = getComputedStyle(document.documentElement).getPropertyValue("--duration-quick");
		const duration = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
			? 0
			: parseFloat(cssDuration) || 150;
		closeTimer.current = setTimeout(() => setPresent(false), duration);
	}, []);

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				hide();
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			if (closeTimer.current) clearTimeout(closeTimer.current);
		};
	}, [hide]);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return peptides;
		return peptides.filter((p) => {
			const name = getPeptideName(p, locale).toLowerCase();
			const cat = getPeptideCategory(p, locale).toLowerCase();
			return name.includes(q) || cat.includes(q) || p.slug.includes(q);
		});
	}, [search, peptides, locale]);

	return (
		<div
			ref={containerRef}
			className="relative w-full max-w-md"
			onKeyDown={(e) => {
				if (e.key === "Escape" && open) {
					e.stopPropagation();
					hide(true);
				}
			}}
		>
			<button
				ref={triggerRef}
				type="button"
				onClick={() => (open ? hide(true) : show())}
				className="hover:bg-accent/30 flex h-11 w-full items-center justify-between gap-2 rounded-md border border-border bg-card px-3 text-left text-sm transition-colors"
				aria-haspopup="dialog"
				aria-expanded={open}
			>
				<span className="flex flex-col">
					<span className="font-medium text-foreground">
						{selected ? getPeptideName(selected, locale) : placeholder}
					</span>
					{selected && (
						<span className="text-[11px] text-muted-foreground">
							{getPeptideCategory(selected, locale)} · {translateTimeUnits(selected.halfLife, locale)}
						</span>
					)}
				</span>
				<ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
			</button>
			{selected && getPeptideHalfLifeSource(selected, locale) && (
				<p
					className="mt-2 break-words text-xs leading-relaxed text-muted-foreground"
					lang={getPeptideHalfLifeSourceLanguage(selected, locale)}
				>
					{getPeptideHalfLifeSource(selected, locale)}
				</p>
			)}

			{present && (
				<div
					className={`tool-peptide-dropdown absolute left-0 right-0 top-full z-50 mt-1 max-h-80 overflow-auto rounded-md border border-border bg-card shadow-lg ${open ? "is-open" : "is-closing"}`}
					data-origin="top-left"
					role="dialog"
					aria-label={placeholder}
					aria-hidden={!open}
					inert={!open}
				>
					<div className="sticky top-0 bg-card p-2">
						<div className="relative">
							<Search
								className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
								aria-hidden="true"
							/>
							<input
								ref={searchRef}
								type="text"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder={searchPlaceholder}
								className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-2 text-sm outline-none"
							/>
						</div>
					</div>

					{filtered.length === 0 ? (
						<p className="px-3 py-4 text-center text-sm text-muted-foreground">{emptyLabel}</p>
					) : (
						<ul>
							{filtered.map((p) => {
								const name = getPeptideName(p, locale);
								const cat = getPeptideCategory(p, locale);
								return (
									<li key={p.slug}>
										<button
											type="button"
											onClick={() => {
												onChange(p.slug);
												hide(true);
											}}
											className={`hover:bg-accent/30 flex w-full flex-col items-start px-3 py-2 text-left text-sm transition-colors ${
												p.slug === value ? "bg-accent/20" : ""
											}`}
										>
											<span className="font-medium text-foreground">{name}</span>
											<span className="text-[11px] text-muted-foreground">
												{cat} · {translateTimeUnits(p.halfLife, locale)}
											</span>
										</button>
									</li>
								);
							})}
						</ul>
					)}
				</div>
			)}
		</div>
	);
}
